/**
 * When an article counts as viewed (Article.views_count).
 *
 * The server used to count every fetch of the article API, and server.js makes
 * one for every page render, so crawlers were over 95% of all "views"
 * (2026-09-26: 61,700 on one article, about 50 of them human). The browser now
 * reports the view itself: only after the tab has been visible for
 * VIEW_DWELL_MS in total, never under automation (navigator.webdriver), and
 * once per article per browser session. The server also ignores bot user
 * agents and counts each viewer once per 30 minutes
 * (django/blog/view_counting.py).
 *
 * Guide, product and comparison pages use the same rules through
 * reportViewWhenRead(). Their ids carry a prefix (`guide:<slug>`) so a
 * session flag never collides with an article's bare slug.
 */
export const VIEW_DWELL_MS = 5000;

export interface ViewStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface VisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export const viewedKey = (slug: string): string => `sd-viewed:${slug}`;

/** Once the dwell has completed: should this browser report a view? Marks it reported. */
export function claimView(slug: string, webdriver: boolean, storage: ViewStorage | null): boolean {
  if (!slug || webdriver) return false;
  try {
    if (storage && storage.getItem(viewedKey(slug))) return false;
    if (storage) storage.setItem(viewedKey(slug), '1');
  } catch {
    // Storage blocked (private mode, sandboxed frame): report anyway. The
    // server's per-viewer dedupe stops repeats.
  }
  return true;
}

/**
 * Call `onViewed` once the page has been visible for `dwellMs` in total;
 * time spent hidden does not count. Returns a cleanup function for useEffect.
 */
export function watchForView(
  onViewed: () => void, doc: VisibilitySource, dwellMs: number = VIEW_DWELL_MS,
): () => void {
  let visibleMs = 0;
  let visibleSince: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let finished = false;

  const stop = () => {
    finished = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    doc.removeEventListener('visibilitychange', onChange);
  };
  const start = () => {
    if (finished || visibleSince !== null || doc.visibilityState !== 'visible') return;
    visibleSince = Date.now();
    timer = setTimeout(() => { stop(); onViewed(); }, Math.max(0, dwellMs - visibleMs));
  };
  const pause = () => {
    if (visibleSince === null) return;
    visibleMs += Date.now() - visibleSince;
    visibleSince = null;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  function onChange() {
    if (doc.visibilityState === 'visible') start(); else pause();
  }

  doc.addEventListener('visibilitychange', onChange);
  start();
  return stop;
}

/**
 * The browser wiring every page shares: after the dwell, claim the view for
 * this session and send it. Use as `useEffect(() => reportViewWhenRead(...))`.
 */
export function reportViewWhenRead(id: string, send: () => void): () => void {
  if (!id || typeof document === 'undefined') return () => {};
  return watchForView(() => {
    let storage: ViewStorage | null = null;
    try { storage = window.sessionStorage; } catch { storage = null; }
    if (claimView(id, navigator.webdriver === true, storage)) send();
  }, document);
}
